// The page side of a presentation's Ask (design/2026-11-03-presentations.md).
// A framed deck posts `ask`; the page holds it here and draws
// PresentationAskDialog. Neither way on sends anything:
// - "Ask in this chat" writes a note into the composer (the breadcrumb and
//   the page's `#/` link) for the person to finish;
// - "Start a new chat from here" keeps the pending seed for the start screen,
//   brings the header's job to the source's job, and opens agent-new.
import { useState, type MutableRefObject } from 'react'
import useRouter from './useRouter'
import useAccount from './useAccount'
import { writePendingSeed, type PendingSeed } from './usePresentationSeed'
import { useWalkthrough, walkthroughSlugOf } from '../help/walkthroughContext'
import { askBreadcrumb, askDraft, innermostLayer, type DeckAsk, type PresentationAskState } from '../agent/presentationFrame'
import type { AgentComposerHandle } from '../agent'

export interface PresentationAskRequest {
  ask: DeckAsk
  customer: string
  /** The host names the source; a message never does. An empty id: not saved yet. */
  source: { kind: 'artifact' | 'dashboard'; id: string }
  /** The source's job: a new chat starts on it. */
  job: string
  /** The page's path in the chat ("data/index.html"), for the in-chat note. Absent on a dashboard. */
  entryPath?: string
  /** Closes what the ask came from (a preview over the chat), once the note is in the composer. */
  closeSource?: () => void
}

/** "Start a new chat from here" on a whole page: a story board's toolbar button, no slide, no figure. */
export interface PageSeedRequest {
  customer: string
  source: { kind: 'artifact' | 'dashboard'; id: string }
  job: string
  /** The page's name, for the start screen's chip. */
  label: string
}

export interface UsePresentationAskOptions {
  /** The chat's composer. Absent where there is no chat (a dashboard): only a new chat is offered. */
  composerHandleRef?: MutableRefObject<AgentComposerHandle | null>
  /** Why this person may not write in this chat (someone else's), or undefined. */
  askInChatRefusal?: string
  /** Why this person may not start a chat (AI access), or undefined. */
  startRefusal?: string
}

export const NOT_SAVED_REASON = 'This presentation is still saving. Try again in a moment.'
/** A share-link viewer who is not signed in (Adam: there are no guest chats). */
export const SIGN_IN_REASON = 'Sign in to Platinum to ask a follow-up'
export const OTHER_CUSTOMER_REASON = 'This presentation belongs to a customer you are not signed in to, so you can’t start a chat from it.'

/**
 * What a published presentation's Ask says to a viewer. Undefined while the
 * answer is not known yet (AI access still loading): the host stays silent
 * and the page waits. Anyone who can open it and may start chats may ask.
 */
export function dashboardAskState({ signedIn, inCustomer, aiAccessLoaded, startRefusal }: {
  signedIn: boolean
  /** The dashboard's customer is one the viewer belongs to. */
  inCustomer: boolean
  aiAccessLoaded: boolean
  /** The AI access refusal for agent_sessions, with who to ask; undefined when allowed. */
  startRefusal?: string
}): PresentationAskState | undefined {
  if (!signedIn) return { enabled: false, reason: SIGN_IN_REASON }
  if (!inCustomer) return { enabled: false, reason: OTHER_CUSTOMER_REASON }
  if (!aiAccessLoaded) return undefined
  return startRefusal ? { enabled: false, reason: startRefusal } : { enabled: true }
}

/** The figure as the dialog and the chip show it: "45% · Total × Q3". */
export function figureLine(ask: DeckAsk): string | undefined {
  const f = ask.figure
  if (!f) return undefined
  const cell = [f.rowLabel || `row ${f.row}`, f.colLabel || `column ${f.col}`].join(' × ')
  return f.text ? `${f.text} · ${cell}` : cell
}

export default function usePresentationAsk({ composerHandleRef, askInChatRefusal, startRefusal }: UsePresentationAskOptions = {}) {
  const router = useRouter()
  const account = useAccount()
  const walkthroughs = useWalkthrough()
  const [request, setRequest] = useState<PresentationAskRequest | null>(null)

  const handleOpen = (next: PresentationAskRequest) => setRequest(next)
  const handleClose = () => setRequest(null)

  // Fills the composer, keeping anything already typed. Never sends.
  const handleAskInChat = () => {
    if (!request || askInChatRefusal) return
    const handle = composerHandleRef?.current
    if (!handle) return
    const note = askDraft(request.ask, request.entryPath ?? '')
    const typed = handle.getInput()
    handle.setInput(typed.trim() ? `${typed.trimEnd()}\n\n${note}` : note)
    request.closeSource?.()
    setRequest(null)
  }

  // Keeps the seed for the start screen, brings the header to the source's
  // job (useAnalysisProfile's pattern: syncJob only loads a job of the
  // customer on screen) and opens the start screen. Nothing is created yet.
  // A walkthrough's step pressing Start tags the seed, so the walkthrough's
  // end clears it. Read now, not from the render: the step's click can come
  // before the run's state reaches this component.
  const startFrom = (customer: string, seed: PendingSeed) => {
    const walkthrough = walkthroughSlugOf(walkthroughs?.current())
    writePendingSeed(customer, walkthrough ? { ...seed, walkthrough } : seed)
    if (seed.job && account.customer?.name === customer && account.job?.name !== seed.job) account.syncJob(seed.job)
    router.navigate('agent-new', { customer })
  }

  const handleStartNewChat = () => {
    if (!request || startRefusal || !request.source.id) return
    const { ask, customer, source, job } = request
    const layer = innermostLayer(ask.at)
    setRequest(null)
    startFrom(customer, {
      source,
      at: layer ? { slide: ask.at.slide, layer } : { slide: ask.at.slide },
      ...(ask.figure ? { figure: { dataset: ask.figure.dataset, row: ask.figure.row, col: ask.figure.col } } : {}),
      job,
      label: askBreadcrumb(ask) || 'Presentation',
      ...(figureLine(ask) ? { figureText: figureLine(ask) } : {}),
      ...(ask.question ? { question: ask.question } : {}),
    })
  }

  // The toolbar's button: the whole page, any story board, deck or not.
  const handleStartChatFromPage = (page: PageSeedRequest) => {
    if (startRefusal || !page.source.id) return
    startFrom(page.customer, { source: page.source, job: page.job, label: page.label || 'Story board' })
  }

  const canAskInChat = !!composerHandleRef
  return {
    request,
    handleOpen,
    handleClose,
    handleAskInChat: canAskInChat ? handleAskInChat : undefined,
    handleStartNewChat,
    handleStartChatFromPage,
    askInChatReason: askInChatRefusal,
    startReason: startRefusal ?? (request && !request.source.id ? NOT_SAVED_REASON : undefined),
    breadcrumb: request ? askBreadcrumb(request.ask) : '',
    figureText: request ? figureLine(request.ask) : undefined,
    question: request?.ask.question,
  }
}

export type PresentationAsk = ReturnType<typeof usePresentationAsk>
